import { createContext, useContext } from 'react';
import { useReducedMotion, type Variants } from 'framer-motion';

/**
 * Shared Framer Motion variants for the landing page's scroll-reveal feel. One vocabulary so every
 * section animates in consistently: a soft fade + lift + de-blur, with an optional stagger for grids.
 *
 * IMPORTANT: every consumer pairs these with {@link useRevealGate} — when the gate is true we pass
 * `false`/`undefined` as the variants (or `initial={false}`) so content is fully visible with NO
 * motion. These variants are only ever applied when entrance motion is allowed.
 */

/**
 * True while the landing is being PRERENDERED to static HTML (the SSG build step,
 * entry-prerender.tsx runs in Node). `useReducedMotion()` alone is NOT enough there: it only reads
 * the device media query (null in Node → falsy) and ignores MotionConfig, so without this gate
 * every `initial="hidden"` would serialize `opacity:0` into dist/landing.html — an invisible page
 * for no-JS readers, which defeats the whole prerender (Tech-Director review of PR #95, finding 1).
 */
const isPrerender = typeof window === 'undefined';

/**
 * True when the SPA booted ON TOP of the prerendered landing markup (set once by main.tsx, consumed
 * once by LandingPage). Entrance reveals are skipped for that first mount so the visitor never sees
 * already-readable static content blink out and re-animate; later in-app navigations back to "/"
 * animate normally.
 */
export const SkipIntroContext = createContext(false);

/**
 * Read main.tsx's one-shot "booted from prerendered markup" flag (false during prerender).
 * PURE read — safe inside a useState initializer. Clearing lives in
 * {@link clearPrerenderedMarkupFlag}, called from a mount EFFECT: reading AND clearing in the
 * initializer was a render-phase side effect, and React's concurrent renderer may run a component's
 * initializer, discard that pass (e.g. around the lazy-chunk Suspense boundary), and render again —
 * the first pass consumed the flag, the retry read false, and prod silently lost skip-intro
 * (observed live on duck-duck-space.com; StrictMode double-invoke would hit the same bug).
 */
export function peekPrerenderedMarkupFlag(): boolean {
  if (isPrerender) return false;
  return (window as unknown as { __duckPrerenderedLanding?: boolean }).__duckPrerenderedLanding === true;
}

/** Clear the one-shot flag once a LandingPage mount has COMMITTED (call from useEffect). */
export function clearPrerenderedMarkupFlag(): void {
  if (isPrerender) return;
  (window as unknown as { __duckPrerenderedLanding?: boolean }).__duckPrerenderedLanding = false;
}

/**
 * The single gate for the landing's ENTRANCE reveals: true ⇒ render content fully visible with no
 * intro motion (device reduced-motion, the SSG prerender, or the first mount over prerendered
 * markup). Ambient/interaction animation must keep using `useReducedMotion()` directly — only the
 * device preference should silence those.
 */
export function useRevealGate(): boolean {
  const reduce = useReducedMotion();
  const skipIntro = useContext(SkipIntroContext);
  return Boolean(reduce) || isPrerender || skipIntro;
}

/** A section/card rising softly into place as it enters the viewport. */
export const revealUp: Variants = {
  hidden: { opacity: 0, y: 28, filter: 'blur(6px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

/** A container that staggers its children's `revealUp` (feature/step grids). */
export const staggerParent: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

/** A child item inside a {@link staggerParent}; a gentler lift than a whole section. */
export const revealItem: Variants = {
  hidden: { opacity: 0, y: 20, filter: 'blur(4px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
};

/** Reveal `whileInView` once — the standard viewport gate for the page's sections. */
export const inViewOnce = { once: true, amount: 0.25 } as const;
